import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth.store';
import { medisSession } from '@/lib/medis/medisSession';
import { endpointUrls } from '@/lib/medis/endpointUrls';
import { usePatientStore } from '@/features/patients/stores/patient.store';
import { beginLogout } from '@/lib/auth/oauth';

/**
 * Logout. Tears down all local session state, then redirects to the auth
 * server's /logout to END the web (session) login — otherwise the next
 * /oauth/authorize would silently re-issue a code from the lingering session
 * and log the user straight back in. The auth server returns to /login.
 */
export function useLogout() {
  const clear = useAuthStore((s) => s.clear);
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['auth', 'logout'],
    mutationFn: async () => {
      /* No backend call — the auth session is ended via the redirect below. */
    },
    onSettled: () => {
      // Set the logout guard + kick the full-page /logout navigation FIRST, so
      // the re-render triggered by clear() (route guard → /login → beginLogin)
      // can't start a competing authorize redirect that cancels this one.
      beginLogout();

      clear();
      // Tear down per-user / session state so the next user starts clean.
      medisSession.invalidate();
      endpointUrls.clear();
      usePatientStore.getState().clear();
      // NOTE: the nomenclature cache (azure_physicians/locations/…) is shared,
      // public catalog data — identical for every user — so it is deliberately
      // NOT wiped on logout (avoids re-sync + the crypto-key churn that used to
      // corrupt the at-rest cache).
      queryClient.clear();
      // The full-page navigation to the auth server's /logout was already kicked
      // off by beginLogout() above.
    },
  });
}
